import React, { FC, useState, useCallback } from "react"
import { Select } from "antd"
import { getRaffles } from "domains/raffles/api"
import { FullRaffle } from "domains/raffles/store/types"
import { useDebouncedCallback } from "use-debounce"

export interface RaffleSelectorProps {
  value?: string
  onChange?: (value: string | undefined) => void
  placeholder?: string
  style?: React.CSSProperties
  allowClear?: boolean
  disabled?: boolean
  mode?: "single" | "multiple" | "tags"
  className?: string
}

export const RaffleSelector: FC<RaffleSelectorProps> = ({
  value,
  onChange,
  placeholder = "Search and select raffle",
  style,
  allowClear = true,
  disabled = false,
  mode = "single",
  className,
}) => {
  const [raffles, setRaffles] = useState<FullRaffle[]>([])
  const [rafflesLoading, setRafflesLoading] = useState(false)

  const loadRaffles = useCallback(async(search: string = "") => {
    setRafflesLoading(true)
    try {
      const response = await getRaffles(1, 10, search ? { title: search } : undefined)
      setRaffles(response.data)
    } catch (error) {
      console.error("Failed to load raffles:", error)
      setRaffles([])
    } finally {
      setRafflesLoading(false)
    }
  }, [])

  const debouncedSearch = useDebouncedCallback((searchValue: string) => {
    loadRaffles(searchValue)
  }, 500)

  const handleRaffleSearch = (searchValue: string) => {
    debouncedSearch(searchValue)
  }

  const handleRaffleFocus = () => {
    if (raffles.length === 0) {
      loadRaffles()
    }
  }

  const handleChange = (selectedValue: string | string[] | undefined) => {
    if (mode === "single") {
      onChange?.(selectedValue as string | undefined)
      if (!selectedValue) {
        loadRaffles()
      }
    } else {
      onChange?.(Array.isArray(selectedValue) ? selectedValue.join(",") : selectedValue)
    }
  }

  return (
    <Select
      mode={mode === "single" ? undefined : mode}
      showSearch
      allowClear={allowClear}
      placeholder={placeholder}
      loading={rafflesLoading}
      value={value}
      onSearch={handleRaffleSearch}
      onFocus={handleRaffleFocus}
      onChange={handleChange}
      filterOption={false}
      style={{ width: "100%", ...style }}
      disabled={disabled}
      className={className}
    >
      {raffles.map(raffle => (
        <Select.Option key={raffle.id} value={raffle.id}>
          {raffle.title} ({raffle.id})
        </Select.Option>
      ))}
    </Select>
  )
}
